/**
 * @import { DOMContainer } from '@restart/ui/useWaitForDOMRef'
 */

import { getRootElement } from '@/config/root';
import { useDevice } from '@/hooks/use-device';
import { Backdrop } from '@/ui/backdrop';
import { CloseIcon } from '@/ui/close-icon';
import { Modal } from '@restart/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { modalId, useModalContext } from '../../context/modal';
import { FiltersForm } from '../filters-form/filters-form';
import classes from './filters-modal.module.css';

/**
 * @param {object} props
 * @param {DOMContainer} [props.container]
 */
export function FiltersModal({ container }) {
  const { isMobile } = useDevice();
  const { activeModalId, setActiveModalId } = useModalContext();
  const [hiding, setHiding] = useState(false);

  const modalContentRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const show = activeModalId === modalId.filters;

  const onClose = useCallback(() => {
    getRootElement()?.dispatchEvent(new Event('filtersClose'));
  }, []);

  const onOpen = useCallback(() => {
    getRootElement()?.dispatchEvent(new Event('filtersOpen'));
  }, []);

  const close = useCallback(() => {
    setActiveModalId(null);
    onClose();
  }, [setActiveModalId, onClose]);

  const handleClose = () => {
    if (!isMobile) {
      close();
      return;
    }

    // Mobile depends on handling the animation end to close
    if (!hiding) {
      setHiding(true);
    }
  };

  // Triggers scroll reset after show for desktop (no animation)
  useEffect(() => {
    if (!isMobile && show) {
      onOpen();
    }
  }, [isMobile, show, onOpen]);

  // Triggers scroll reset after animation ends
  useEffect(() => {
    const controller = new AbortController();
    modalContentRef.current?.addEventListener(
      'animationend',
      () => {
        if (hiding) {
          close();
          setHiding(false);
        } else {
          onOpen();
        }
      },
      { signal: controller.signal }
    );
    return () => controller.abort();
  }, [show, hiding, close, onOpen]);

  return (
    <Modal
      container={container}
      backdrop={!isMobile}
      renderBackdrop={isMobile ? undefined : Backdrop}
      show={show}
      className={classes.modal}
      aria-label="Filters"
    >
      <div ref={modalContentRef} className={`${classes.modalContent} ${hiding ? classes.modalContentHide : ''}`}>
        <button type="button" className={classes.btnClose} aria-label="Cancel editing filters" onClick={handleClose}>
          <CloseIcon />
        </button>
        <FiltersForm filtersChange={handleClose} />
      </div>
    </Modal>
  );
}
